import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useResolvedDaycareHotelClinicId } from "@/hooks/useResolvedDaycareHotelClinicId";

export interface OvernightConfig {
  id: string | null;
  isEnabled: boolean;
  /** "HH:mm" — the DB stores a `time`, which PostgREST returns as "HH:mm:ss". */
  checkoutTime: string | null;
  price: number | null;
}

export interface OvernightConfigInput {
  isEnabled?: boolean;
  checkoutTime?: string | null;
  price?: number | null;
}

const EMPTY: OvernightConfig = { id: null, isEnabled: false, checkoutTime: null, price: null };

const toHourMinute = (value: string | null): string | null => (value ? value.slice(0, 5) : null);

export const overnightConfigKey = (clinicId: string | null) => ["overnight-config", clinicId];

export function useOvernightConfig() {
  const { clinicId, isLoading: isLoadingClinicId } = useResolvedDaycareHotelClinicId();

  return useQuery({
    queryKey: overnightConfigKey(clinicId),
    queryFn: async (): Promise<OvernightConfig> => {
      if (!clinicId) return EMPTY;
      const { data, error } = await supabase
        .from("overnight_config")
        .select("id, is_enabled, checkout_time, price")
        .eq("clinic_id", clinicId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return EMPTY;
      return {
        id: data.id,
        isEnabled: data.is_enabled,
        checkoutTime: toHourMinute(data.checkout_time),
        price: data.price,
      };
    },
    enabled: !!clinicId && !isLoadingClinicId,
  });
}

export function useSaveOvernightConfig() {
  const queryClient = useQueryClient();
  const { clinicId } = useResolvedDaycareHotelClinicId();

  return useMutation({
    mutationFn: async (input: OvernightConfigInput) => {
      if (!clinicId) throw new Error("no_clinic_selected");
      const { error } = await supabase.from("overnight_config").upsert(
        {
          clinic_id: clinicId,
          ...(input.isEnabled !== undefined ? { is_enabled: input.isEnabled } : {}),
          ...(input.checkoutTime !== undefined
            ? { checkout_time: input.checkoutTime ? `${input.checkoutTime}:00` : null }
            : {}),
          ...(input.price !== undefined ? { price: input.price } : {}),
        },
        { onConflict: "clinic_id" },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overnightConfigKey(clinicId) });
    },
  });
}
